import axios from 'axios'
import { createCompiler } from '@nsftx/seven-client-retail-print-template-engine'
import type PrintOptions from '../interfaces/PrintOptions'
import type PrintDriver from '../interfaces/PrintDriver'
import type PrintContext from '../interfaces/PrintContext'
import type PrintMeta from '../interfaces/PrintMeta'
import { prepareTemplate } from './printTemplatesService'
import { usePrintConfigStore } from '../stores/printConfigStore'
import { numberToFixed } from '../utils/numberFilter'
import { createNxgFormatNumberFilter } from '../utils/nxgFormatNumberFilter'

const spaces: Record<
  string,
  {
    compiler: ReturnType<typeof createCompiler>
  }
> = {}

interface PrintMetaWithTimestamp extends PrintMeta {
  printedAt: string
}

interface PrintContextWithTimestamp<Model> extends PrintContext<Model> {
  meta: PrintMetaWithTimestamp
}

const createPrint = (options: PrintOptions) => {
  const config = options
  let driver: PrintDriver | null = null
  const init = (config: PrintOptions) => {
    driver = config.driver

    const nxgFormatNumberFilter = createNxgFormatNumberFilter({
      numberFormat: config.numberFormat,
      currency: config.currency,
      currencyDisplay: config.currencyDisplay
    })

    Object.entries(config.spaces).forEach(([key, value]) => {
      // prepare templates, add intro and outro, if missing
      for (const [templateKey, templateValue] of Object.entries(value.compilerOptions.engineConfig.templates)) {
        value.compilerOptions.engineConfig.templates[templateKey] = prepareTemplate(templateValue)
      }

      const compiler = createCompiler(value.compilerOptions)
      compiler.registerFilter('nxg_to_fixed', numberToFixed)
      compiler.registerFilter('nxg_format_number', nxgFormatNumberFilter)
      spaces[key] = {
        compiler
      }
    })
  }

  const performPrint = async <T>(scope: string, template: string, context: PrintContext<T>) => {
    if (config.checkStatusBeforePrint) {
      await driver?.getPrinterStatus()
    }

    const printedAt = new Date().toISOString()

    const extendedContext: PrintContextWithTimestamp<T> = {
      ...context,
      meta: {
        ...context.meta,
        printedAt
      }
    }

    const rawTemplate = spaces[scope].compiler.getTemplateSource(template)
    const renderedTemplate = await spaces[scope].compiler.renderTemplate(template, extendedContext)
    const result = await driver?.addJob(renderedTemplate, extendedContext, rawTemplate)

    if (config.checkStatusAfterPrint) {
      await driver?.getPrinterStatus()
    }

    return result
  }

  const getCompiler = (space: string) => {
    return spaces[space]?.compiler
  }

  const updateTemplate = (space: string, templateName: string, template: string) => {
    const preparedTemplate = prepareTemplate(template)
    getCompiler(space)?.updateTemplate(templateName, preparedTemplate)
  }

  const updateGlobals = (space: string, globals: Record<string, string | object>) => {
    getCompiler(space)?.updateGlobals(globals)
  }

  init(options)

  return {
    performPrint,
    getCompiler,
    updateTemplate,
    updateGlobals
  }
}

const getImagePreview = async (template: string) => {
  const printConfigStore = usePrintConfigStore()
  const chars = printConfigStore.paperWidth.toString()
  return axios.post<Blob>(
    `${import.meta.env.VITE_PREVIEWER_API}add_job`,
    {
      template,
      config: 0,
      printer: 'blob',
      chars,
      withCredentials: false
    },
    {
      responseType: 'blob'
    }
  )
}

export default { getImagePreview, createPrint }
